import type {
  LockerverseCatalog,
  LockerverseCheckoutClient,
  LockerverseCheckoutEstimate,
  LockerverseCheckoutItem,
  LockerversePreparedCheckout,
} from "@lockerverse/sdk/checkout";
import {
  AddressElement,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type { StripeAddressElementChangeEvent } from "@stripe/stripe-js";
import {
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AdditionalCheckoutFields,
  type CheckoutFieldValues,
  CheckoutToggle,
  ServiceFeeField,
  TipField,
} from "./checkout-fields.tsx";
import {
  createCustomFieldValues,
  validateCustomFields,
} from "./custom-fields.tsx";
import { EmailField, FormField } from "./form-field.tsx";
import { FormSkeleton } from "./form-skeleton.tsx";
import { formatMoney } from "./format-money.ts";
import {
  createPaymentFormState,
  getPaymentUiState,
  type PaymentFormAction,
  type PaymentFormState,
  type PaymentUiState,
  paymentFormReducer,
} from "./payment-form-state.ts";
import type { CheckoutAddresses } from "./payment-submission.ts";
import { validateContact } from "./payment-validation.ts";
import { getInternationalPhone } from "./phone.ts";
import type { PhoneInputValue } from "./phone-field.tsx";
import { PhoneInput } from "./phone-field.tsx";
import { useStripeAppearance } from "./stripe-appearance.ts";
import { defaultLockerverseStripeLoader as cachedStripeLoader } from "./stripe-loader.ts";
import type { LockerversePaymentProps } from "./types.ts";
import { usePaymentFormController } from "./use-payment-form-controller.ts";

export const defaultLockerverseStripeLoader = cachedStripeLoader;

export function preloadLockerversePayment(
  catalog: Pick<LockerverseCatalog, "payment">
) {
  const { connectedAccountId, publishableKey } = catalog.payment;
  return defaultLockerverseStripeLoader(
    publishableKey,
    connectedAccountId ? { stripeAccount: connectedAccountId } : undefined
  );
}

function getRecurringCadence(
  paymentOption: LockerversePreparedCheckout["paymentOption"]
) {
  if (paymentOption === "monthly") {
    return { interval: "month", label: "Monthly" } as const;
  }
  if (paymentOption === "annually") {
    return { interval: "year", label: "Annual" } as const;
  }
  return null;
}

function getConnectedAccountId(
  catalog: LockerverseCatalog,
  quote: LockerverseCheckoutEstimate | LockerversePreparedCheckout,
  resumePayment: LockerversePaymentProps["resumePayment"]
) {
  if (resumePayment) {
    const { connectedAccountId } = resumePayment;
    return connectedAccountId;
  }
  if ("payment" in quote) {
    const { connectedAccountId } = quote.payment;
    return connectedAccountId;
  }
  const { connectedAccountId } = catalog.payment;
  return connectedAccountId;
}

export function ReadyPayment({
  accentColor,
  catalog,
  checkoutItems,
  client,
  appearanceRoot,
  metadata,
  onPaymentComplete,
  onResumePaymentFailed,
  onPaymentRecoveryChange,
  onPaymentUncertain,
  onQuoteChange,
  quote,
  resumePayment,
  onStateChange,
  stripeLoader,
  theme,
  visualStyle,
}: {
  accentColor: string;
  catalog: LockerverseCatalog;
  checkoutItems: LockerverseCheckoutItem[];
  client: LockerverseCheckoutClient;
  appearanceRoot: RefObject<HTMLElement | null>;
  metadata: Record<string, string> | undefined;
  onPaymentComplete: LockerversePaymentProps["onPaymentComplete"];
  onResumePaymentFailed: () => void;
  onPaymentRecoveryChange: LockerversePaymentProps["onPaymentRecoveryChange"];
  onPaymentUncertain: LockerversePaymentProps["onPaymentUncertain"];
  onQuoteChange: (quote: LockerversePreparedCheckout, notice?: string) => void;
  quote: LockerverseCheckoutEstimate | LockerversePreparedCheckout;
  resumePayment: LockerversePaymentProps["resumePayment"];
  onStateChange: (state: PaymentUiState) => void;
  stripeLoader: NonNullable<LockerversePaymentProps["stripeLoader"]>;
  theme: LockerversePaymentProps["theme"];
  visualStyle: LockerversePaymentProps["style"];
}) {
  const [paymentState, setPaymentState] = useState(() =>
    createPaymentFormState(Boolean(resumePayment))
  );
  const paymentStateRef = useRef(paymentState);
  paymentStateRef.current = paymentState;
  const updateState = useCallback(
    (action: PaymentFormAction, notifyHost = false) => {
      const next = paymentFormReducer(paymentStateRef.current, action);
      if (next === paymentStateRef.current) {
        return;
      }
      paymentStateRef.current = next;
      setPaymentState(next);
      if (notifyHost) {
        onStateChange(getPaymentUiState(next));
      }
    },
    [onStateChange]
  );
  const requestedConnectedAccountId = getConnectedAccountId(
    catalog,
    quote,
    resumePayment
  );
  const stripeConfigurationRef = useRef({
    connectedAccountId: requestedConnectedAccountId,
    paymentOption: quote.paymentOption,
  });
  if (
    paymentState.status !== "submitting" &&
    paymentState.status !== "recovering"
  ) {
    stripeConfigurationRef.current = {
      connectedAccountId: requestedConnectedAccountId,
      paymentOption: quote.paymentOption,
    };
  }
  const { connectedAccountId, paymentOption } = stripeConfigurationRef.current;
  const stripe = useMemo(
    () =>
      stripeLoader(
        catalog.payment.publishableKey,
        connectedAccountId ? { stripeAccount: connectedAccountId } : undefined
      ).catch(() => null),
    [catalog.payment.publishableKey, connectedAccountId, stripeLoader]
  );
  const [stripeLoadFailed, setStripeLoadFailed] = useState(false);
  useEffect(() => {
    let active = true;
    setStripeLoadFailed(false);
    stripe.then((loadedStripe) => {
      if (active && loadedStripe === null) {
        setStripeLoadFailed(true);
      }
    });
    return () => {
      active = false;
    };
  }, [stripe]);

  const appearance = useStripeAppearance(
    appearanceRoot,
    theme,
    visualStyle,
    paymentState.status === "submitting" ||
      paymentState.status === "recovering",
    accentColor
  );
  if (!appearance) {
    return <FormSkeleton fieldCount={3} label="Loading payment form" />;
  }

  return (
    <Elements
      key={`${catalog.payment.publishableKey}:${connectedAccountId ?? "platform"}:${paymentOption}`}
      options={{
        amount: Math.max(quote.totalCents, 100),
        appearance,
        currency: catalog.payment.currency,
        excludedPaymentMethodTypes: ["us_bank_account"],
        mode: paymentOption === "one-time" ? "payment" : "subscription",
        syncAddressCheckbox: "none",
      }}
      stripe={stripe}
    >
      <PaymentForm
        catalog={catalog}
        checkoutItems={checkoutItems}
        client={client}
        metadata={metadata}
        onPaymentComplete={onPaymentComplete}
        onPaymentRecoveryChange={onPaymentRecoveryChange}
        onPaymentUncertain={onPaymentUncertain}
        onQuoteChange={onQuoteChange}
        onResumePaymentFailed={onResumePaymentFailed}
        onStateChange={onStateChange}
        paymentState={paymentState}
        quote={quote}
        resumePayment={resumePayment}
        stripeConnectedAccountId={connectedAccountId}
        stripeLoadFailed={stripeLoadFailed}
        updateState={updateState}
      />
    </Elements>
  );
}

function StripeElementLoader({ label }: { label: string }) {
  return (
    <div className="lockerverse-payment__stripe-loader" role="status">
      <p>{label}</p>
      <div aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function StripePaymentField({
  onError,
  onReady,
  ready,
}: {
  onError: (message: string) => void;
  onReady: () => void;
  ready: boolean;
}) {
  return (
    <div className="lockerverse-payment__stripe-element" data-ready={ready}>
      {ready ? null : <StripeElementLoader label="Loading payment form..." />}
      <div className="lockerverse-payment__stripe-mount">
        <PaymentElement
          onLoadError={({ error }) =>
            onError(error.message ?? "Payment form failed to load.")
          }
          onReady={onReady}
          options={{
            business: { name: "Lockerverse" },
            fields: {
              billingDetails: {
                address: "never",
                email: "never",
                name: "never",
                phone: "never",
              },
            },
            layout: "tabs",
            terms: {
              applePay: "auto",
              auBecsDebit: "never",
              bancontact: "never",
              card: "always",
              cashapp: "never",
              googlePay: "never",
              ideal: "never",
              paypal: "auto",
              sepaDebit: "never",
              sofort: "never",
              usBankAccount: "never",
            },
            wallets: { applePay: "auto", googlePay: "never" },
          }}
        />
      </div>
    </div>
  );
}

function StripeAddressField({
  label,
  mode,
  onChange,
  onError,
  onReady,
  ready,
}: {
  label: string;
  mode: "billing" | "shipping";
  onChange: (address: StripeAddressElementChangeEvent) => void;
  onError: (message: string) => void;
  onReady: () => void;
  ready: boolean;
}) {
  return (
    <div className="lockerverse-payment__address">
      <h2>{label}</h2>
      <div
        className="lockerverse-payment__stripe-element lockerverse-payment__stripe-element--address"
        data-ready={ready}
      >
        {ready ? null : (
          <StripeElementLoader label={`Loading ${label.toLowerCase()}...`} />
        )}
        <div className="lockerverse-payment__stripe-mount">
          <AddressElement
            onChange={onChange}
            onLoadError={({ error }) =>
              onError(error.message ?? `${label} failed to load.`)
            }
            onReady={onReady}
            options={{ autocomplete: { mode: "automatic" }, mode }}
          />
        </div>
      </div>
    </div>
  );
}

function PaymentErrorMessage({ message }: { message: string | undefined }) {
  if (!message) {
    return null;
  }
  return (
    <p className="lockerverse-payment__field-message" role="alert">
      {message}
    </p>
  );
}

function StripeCheckoutFields({
  catalog,
  displayedTotalCents,
  fieldValues,
  isBillingAddressReady,
  isPaymentReady,
  isShippingAddressReady,
  onAddressChange,
  onFieldValuesChange,
  onStripeLoadError,
  setIsBillingAddressReady,
  setIsPaymentReady,
  setIsShippingAddressReady,
}: {
  catalog: LockerverseCatalog;
  displayedTotalCents: number;
  fieldValues: CheckoutFieldValues;
  isBillingAddressReady: boolean;
  isPaymentReady: boolean;
  isShippingAddressReady: boolean;
  onAddressChange: (
    mode: "billing" | "shipping",
    address: StripeAddressElementChangeEvent | null
  ) => void;
  onFieldValuesChange: (values: CheckoutFieldValues) => void;
  onStripeLoadError: (message: string) => void;
  setIsBillingAddressReady: (ready: boolean) => void;
  setIsPaymentReady: (ready: boolean) => void;
  setIsShippingAddressReady: (ready: boolean) => void;
}) {
  const requiresPayment = displayedTotalCents > 0;

  return (
    <>
      {requiresPayment ? (
        <StripePaymentField
          onError={onStripeLoadError}
          onReady={() => setIsPaymentReady(true)}
          ready={isPaymentReady}
        />
      ) : null}
      {requiresPayment || catalog.widget.fields.shipping ? (
        <StripeAddressField
          label={requiresPayment ? "Billing address" : "Shipping address"}
          mode="billing"
          onChange={(address) => onAddressChange("billing", address)}
          onError={onStripeLoadError}
          onReady={() => setIsBillingAddressReady(true)}
          ready={isBillingAddressReady}
        />
      ) : null}
      {catalog.widget.fields.shipping ? (
        <div className="lockerverse-payment__shipping">
          <CheckoutToggle
            checked={fieldValues.shippingMatchesBilling}
            name="shippingMatchesBilling"
            onChange={(shippingMatchesBilling) => {
              onFieldValuesChange({
                ...fieldValues,
                shippingMatchesBilling,
              });
              if (shippingMatchesBilling) {
                onAddressChange("shipping", null);
              } else {
                setIsShippingAddressReady(false);
              }
            }}
          >
            Shipping is same as billing information
          </CheckoutToggle>
          {fieldValues.shippingMatchesBilling ? null : (
            <StripeAddressField
              label="Shipping address"
              mode="shipping"
              onChange={(address) => onAddressChange("shipping", address)}
              onError={onStripeLoadError}
              onReady={() => setIsShippingAddressReady(true)}
              ready={isShippingAddressReady}
            />
          )}
        </div>
      ) : null}
    </>
  );
}

function PaymentSummary({
  displayedBaseCents,
  displayedTipCents,
  displayedTotalCents,
  quote,
}: {
  displayedBaseCents: number;
  displayedTipCents: number;
  displayedTotalCents: number;
  quote: LockerverseCheckoutEstimate | LockerversePreparedCheckout;
}) {
  const cadence = getRecurringCadence(quote.paymentOption);
  const displayedServiceFeeCents = Math.max(
    0,
    displayedBaseCents - quote.subtotalCents + quote.discountCents
  );
  return (
    <section
      aria-label="Payment summary"
      className="lockerverse-payment__payment-summary"
    >
      <h2>Order summary</h2>
      <div className="lockerverse-payment__payment-summary-items">
        {quote.lineItems.map((item) => (
          <div
            className="lockerverse-payment__payment-summary-row"
            key={item.listingId}
          >
            <span>
              {item.productName}
              {item.quantity > 1 ? (
                <>
                  {" "}
                  <small>× {item.quantity}</small>
                </>
              ) : null}
              {item.paymentOption === "monthly" ? (
                <small> · Monthly</small>
              ) : null}
              {item.paymentOption === "annually" ? (
                <small> · Annual</small>
              ) : null}
            </span>
            <strong>{formatMoney(item.subtotalCents)}</strong>
          </div>
        ))}
      </div>
      <div className="lockerverse-payment__payment-summary-totals">
        {displayedServiceFeeCents > 0 ? (
          <div className="lockerverse-payment__payment-summary-row lockerverse-payment__payment-summary-detail">
            <span>Service fee</span>
            <strong>{formatMoney(displayedServiceFeeCents)}</strong>
          </div>
        ) : null}
        {quote.discountCents > 0 && quote.appliedDiscount ? (
          <div className="lockerverse-payment__payment-summary-row lockerverse-payment__payment-summary-discount">
            <span>
              {quote.appliedDiscount.source === "email_list"
                ? "Email discount"
                : "Promo code discount"}{" "}
              ({quote.appliedDiscount.percentageOff}% off)
            </span>
            <strong>-{formatMoney(quote.discountCents)}</strong>
          </div>
        ) : null}
        {displayedTipCents > 0 ? (
          <div className="lockerverse-payment__payment-summary-row lockerverse-payment__payment-summary-detail lockerverse-payment__payment-summary-tip">
            <span>Tip</span>
            <strong>{formatMoney(displayedTipCents)}</strong>
          </div>
        ) : null}
        <div className="lockerverse-payment__payment-summary-row lockerverse-payment__payment-summary-total">
          <span>{cadence ? `${cadence.label} total` : "Total"}</span>
          <strong>{formatMoney(displayedTotalCents)}</strong>
        </div>
      </div>
    </section>
  );
}

function PaymentAction({
  catalog,
  displayedTotalCents,
  fieldValues,
  isBillingAddressReady,
  isPaymentReady,
  isRequiredShippingReady,
  isSubmitting,
  onFieldValuesChange,
  quote,
}: {
  catalog: LockerverseCatalog;
  displayedTotalCents: number;
  fieldValues: CheckoutFieldValues;
  isBillingAddressReady: boolean;
  isPaymentReady: boolean;
  isRequiredShippingReady: boolean;
  isSubmitting: boolean;
  onFieldValuesChange: (values: CheckoutFieldValues) => void;
  quote: LockerverseCheckoutEstimate | LockerversePreparedCheckout;
}) {
  const isFree = displayedTotalCents === 0;
  const isReady = isFree
    ? !catalog.widget.fields.shipping ||
      (isBillingAddressReady && isRequiredShippingReady)
    : isPaymentReady && isBillingAddressReady && isRequiredShippingReady;
  const cadence = getRecurringCadence(quote.paymentOption);
  let label = isFree
    ? "Complete order"
    : `Pay ${formatMoney(displayedTotalCents)}`;
  if (!isFree && cadence) {
    label = `Subscribe for ${formatMoney(displayedTotalCents)}/${cadence.interval}`;
  }
  if (isSubmitting) {
    label = isFree ? "Completing…" : "Processing…";
  }
  let actionLabel = `Process payment for ${formatMoney(displayedTotalCents)} total`;
  if (isFree) {
    actionLabel = "Complete order";
  } else if (cadence) {
    actionLabel = `Start ${cadence.label.toLowerCase()} subscription for ${formatMoney(displayedTotalCents)}`;
  }
  return (
    <div className="lockerverse-payment__actions">
      <ServiceFeeField
        amountCents={
          quote.serviceFeeCents ||
          Math.round(
            (quote.subtotalCents *
              catalog.pricingPolicy.serviceFeeRateBasisPoints) /
              10_000
          )
        }
        enabled={catalog.widget.fields.serviceFee}
        onChange={onFieldValuesChange}
        values={fieldValues}
      />
      <button
        aria-busy={isSubmitting}
        aria-label={actionLabel}
        disabled={isSubmitting || !isReady}
        type="submit"
      >
        {label}
      </button>
    </div>
  );
}

function focusNamedFormControl(form: HTMLFormElement, name: string) {
  const control = form.elements.namedItem(name);
  if (control instanceof HTMLElement) {
    control.focus();
    return;
  }
  if (control instanceof RadioNodeList) {
    Array.from(control)
      .find((item) => item instanceof HTMLElement)
      ?.focus();
  }
}

function focusFirstValidationError({
  customFieldErrors,
  emailRef,
  form,
  parsedTipSuccess,
  phoneRef,
  validationErrors,
}: {
  customFieldErrors: Record<string, string>;
  emailRef: React.RefObject<HTMLInputElement | null>;
  form: HTMLFormElement;
  parsedTipSuccess: boolean;
  phoneRef: React.RefObject<HTMLInputElement | null>;
  validationErrors: { email?: string; phone?: string };
}) {
  if (validationErrors.email) {
    emailRef.current?.focus();
    return;
  }
  if (validationErrors.phone) {
    phoneRef.current?.focus();
    return;
  }
  if (!parsedTipSuccess) {
    form.querySelector<HTMLElement>('[name="tip"]')?.focus();
    return;
  }
  const [firstFieldId] = Object.keys(customFieldErrors);
  if (firstFieldId) {
    focusNamedFormControl(form, `custom:${firstFieldId}`);
  }
}

function ContactFields({
  email,
  emailDiscountPercentOff,
  emailErrorId,
  emailId,
  emailRef,
  errors,
  onEmailBlur,
  onEmailChange,
  onPhoneChange,
  phone,
  phoneCountry,
  phoneEnabled,
  phoneErrorId,
  phoneId,
  phoneRef,
}: {
  email: string;
  emailDiscountPercentOff: number | undefined;
  emailErrorId: string;
  emailId: string;
  emailRef: React.RefObject<HTMLInputElement | null>;
  errors: { email?: string; phone?: string };
  onEmailBlur: () => void;
  onEmailChange: (value: string) => void;
  onPhoneChange: (value: PhoneInputValue) => void;
  phone: string;
  phoneCountry: string;
  phoneEnabled: boolean;
  phoneErrorId: string;
  phoneId: string;
  phoneRef: React.RefObject<HTMLInputElement | null>;
}) {
  return (
    <div className="lockerverse-payment__contact">
      <EmailField
        error={errors.email}
        errorId={emailErrorId}
        id={emailId}
        label="Email"
        name="email"
        onBlur={onEmailBlur}
        onChange={(event) => onEmailChange(event.currentTarget.value)}
        ref={emailRef}
        required
        value={email}
      />
      {emailDiscountPercentOff === undefined ? null : (
        <p className="lockerverse-payment__discount-message" role="status">
          Your email qualifies for {emailDiscountPercentOff}% off.
        </p>
      )}
      {phoneEnabled ? (
        <FormField
          error={errors.phone}
          errorId={phoneErrorId}
          id={phoneId}
          label="Phone"
        >
          <PhoneInput
            country={phoneCountry}
            error={errors.phone}
            errorId={phoneErrorId}
            id={phoneId}
            inputRef={phoneRef}
            name="phone"
            onChange={onPhoneChange}
            required
            value={phone}
          />
        </FormField>
      ) : null}
    </div>
  );
}

function PaymentForm({
  catalog,
  checkoutItems,
  client,
  metadata,
  onPaymentComplete,
  onResumePaymentFailed,
  onPaymentRecoveryChange,
  onPaymentUncertain,
  onQuoteChange,
  onStateChange,
  quote,
  resumePayment,
  stripeConnectedAccountId,
  stripeLoadFailed,
  paymentState,
  updateState,
}: {
  catalog: LockerverseCatalog;
  checkoutItems: LockerverseCheckoutItem[];
  client: LockerverseCheckoutClient;
  metadata: Record<string, string> | undefined;
  onPaymentComplete: LockerversePaymentProps["onPaymentComplete"];
  onResumePaymentFailed: () => void;
  onPaymentRecoveryChange: LockerversePaymentProps["onPaymentRecoveryChange"];
  onPaymentUncertain: LockerversePaymentProps["onPaymentUncertain"];
  onQuoteChange: (quote: LockerversePreparedCheckout, notice?: string) => void;
  onStateChange: (state: PaymentUiState) => void;
  quote: LockerverseCheckoutEstimate | LockerversePreparedCheckout;
  resumePayment: LockerversePaymentProps["resumePayment"];
  stripeConnectedAccountId: string | null;
  stripeLoadFailed: boolean;
  paymentState: PaymentFormState;
  updateState: (action: PaymentFormAction, notifyHost?: boolean) => void;
}) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("US");
  const [errors, setErrors] = useState<{ email?: string; phone?: string }>({});
  const [customFieldErrors, setCustomFieldErrors] = useState<
    Record<string, string>
  >({});
  const [tipError, setTipError] = useState<string>();
  const [isPaymentReady, setIsPaymentReady] = useState(false);
  const [isBillingAddressReady, setIsBillingAddressReady] = useState(false);
  const [isShippingAddressReady, setIsShippingAddressReady] = useState(false);
  const [fieldValues, setFieldValues] = useState<CheckoutFieldValues>({
    customFields: createCustomFieldValues(catalog.widget.customFields),
    customTip: "",
    includeServiceFee: catalog.widget.fields.serviceFee,
    memberId: "",
    merchSize: "M",
    promoCode: "",
    shippingMatchesBilling: true,
    tipPercent: "10",
  });
  const addressesRef = useRef<CheckoutAddresses>({
    billing: null,
    shipping: null,
  });
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const statusRef = useRef<HTMLParagraphElement>(null);
  const paymentWasRequiredRef = useRef(quote.totalCents > 0);
  const elements = useElements();
  const stripe = useStripe();
  const controller = usePaymentFormController({
    catalog,
    checkoutItems,
    client,
    elements,
    email,
    fieldValues,
    metadata,
    onPaymentComplete,
    onPaymentRecoveryChange,
    onPaymentUncertain,
    onQuoteChange,
    onResumePaymentFailed,
    onStateChange,
    paymentState,
    quote,
    resumePayment,
    stripe,
    stripeConnectedAccountId,
    stripeLoadFailed,
    updateState,
  });
  const {
    displayedTotalCents,
    isRecovering,
    isRefreshingQuote,
    isSubmitting,
    parsedTip,
    paymentError,
    promoError,
    terminalMessage,
  } = controller.state;
  const idPrefix = `lockerverse-${useId()}`;
  const emailId = `${idPrefix}-email`;
  const emailErrorId = `${emailId}-error`;
  const phoneId = `${idPrefix}-phone`;
  const phoneErrorId = `${phoneId}-error`;
  const tipErrorId = `${idPrefix}-tip-error`;
  const parsedTipCents = parsedTip.success ? parsedTip.cents : null;
  const displayedTipCents = parsedTipCents ?? 0;
  const displayedBaseCents = displayedTotalCents - displayedTipCents;
  const isRequiredShippingReady =
    !catalog.widget.fields.shipping ||
    fieldValues.shippingMatchesBilling ||
    isShippingAddressReady;

  useEffect(() => {
    if (isSubmitting) {
      statusRef.current?.focus({ preventScroll: true });
    }
  }, [isSubmitting]);

  useEffect(() => {
    if (!(isSubmitting || errors.email || !errors.phone)) {
      phoneRef.current?.focus();
    }
  }, [errors.email, errors.phone, isSubmitting]);

  useEffect(() => {
    const paymentIsRequired = displayedTotalCents > 0;
    if (paymentIsRequired && !paymentWasRequiredRef.current) {
      setIsPaymentReady(false);
      setIsBillingAddressReady(false);
    }
    paymentWasRequiredRef.current = paymentIsRequired;
  }, [displayedTotalCents]);

  function clearError(field: "email" | "phone") {
    controller.commands.fieldChanged(false, field !== "email");
    setErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
  }

  function handleFieldValuesChange(nextValues: CheckoutFieldValues) {
    const promoChanged = nextValues.promoCode !== fieldValues.promoCode;
    const serviceFeeChanged =
      nextValues.includeServiceFee !== fieldValues.includeServiceFee;
    controller.commands.fieldChanged(
      promoChanged,
      !(promoChanged || serviceFeeChanged)
    );
    setFieldValues(nextValues);
    setCustomFieldErrors({});
    setTipError(undefined);
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const internationalPhone = catalog.widget.fields.phone
      ? getInternationalPhone(phone, phoneCountry)
      : "";
    const validation = validateContact(
      { email, phone: internationalPhone },
      catalog.widget.fields.phone
    );
    const nextCustomFieldErrors = validateCustomFields(
      catalog.widget.customFields,
      fieldValues.customFields
    );
    if (
      !(validation.value && parsedTip.success) ||
      Object.keys(nextCustomFieldErrors).length > 0
    ) {
      setErrors(validation.errors);
      setCustomFieldErrors(nextCustomFieldErrors);
      setTipError(parsedTip.success ? undefined : parsedTip.message);
      controller.commands.reportValidationFailure();
      const form = event.currentTarget;
      queueMicrotask(() => {
        focusFirstValidationError({
          customFieldErrors: nextCustomFieldErrors,
          emailRef,
          form,
          parsedTipSuccess: parsedTip.success,
          phoneRef,
          validationErrors: validation.errors,
        });
      });
      return;
    }

    setErrors({});
    setCustomFieldErrors({});

    const result = await controller.commands.submit({
      addresses: addressesRef.current,
      phone: internationalPhone,
    });
    if (result?.phoneError) {
      setErrors({ phone: result.phoneError });
    }
  }

  if (terminalMessage) {
    return (
      <p
        className="lockerverse-payment__payment-status"
        ref={statusRef}
        role="status"
        tabIndex={-1}
      >
        {terminalMessage}
      </p>
    );
  }

  if (isRecovering) {
    return (
      <p className="lockerverse-payment__payment-status" role="status">
        Recovering payment…
      </p>
    );
  }

  return (
    <form
      aria-busy={isSubmitting}
      className="lockerverse-payment__form"
      noValidate
      onSubmit={handleSubmit}
    >
      <div
        data-lockerverse-input-region=""
        inert={isSubmitting ? true : undefined}
      >
        <fieldset
          className="lockerverse-payment__input-fieldset"
          disabled={isSubmitting}
        >
          <PaymentSummary
            displayedBaseCents={displayedBaseCents}
            displayedTipCents={displayedTipCents}
            displayedTotalCents={displayedTotalCents}
            quote={quote}
          />
          <TipField
            enabled={catalog.widget.fields.tipping}
            error={tipError}
            errorId={tipErrorId}
            onChange={handleFieldValuesChange}
            values={fieldValues}
          />
          <ContactFields
            email={email}
            emailDiscountPercentOff={
              quote.appliedDiscount?.source === "email_list"
                ? quote.appliedDiscount.percentageOff
                : undefined
            }
            emailErrorId={emailErrorId}
            emailId={emailId}
            emailRef={emailRef}
            errors={errors}
            onEmailBlur={() => controller.commands.refreshAuthoritativeTotal()}
            onEmailChange={(value) => {
              setEmail(value);
              clearError("email");
            }}
            onPhoneChange={({ value, country }) => {
              setPhone(value);
              setPhoneCountry(country);
              clearError("phone");
            }}
            phone={phone}
            phoneCountry={phoneCountry}
            phoneEnabled={catalog.widget.fields.phone}
            phoneErrorId={phoneErrorId}
            phoneId={phoneId}
            phoneRef={phoneRef}
          />
          <StripeCheckoutFields
            catalog={catalog}
            displayedTotalCents={displayedTotalCents}
            fieldValues={fieldValues}
            isBillingAddressReady={isBillingAddressReady}
            isPaymentReady={isPaymentReady}
            isShippingAddressReady={isShippingAddressReady}
            onAddressChange={(mode, address) => {
              addressesRef.current[mode] = address;
            }}
            onFieldValuesChange={handleFieldValuesChange}
            onStripeLoadError={controller.commands.reportStripeLoadError}
            setIsBillingAddressReady={setIsBillingAddressReady}
            setIsPaymentReady={setIsPaymentReady}
            setIsShippingAddressReady={setIsShippingAddressReady}
          />
          <AdditionalCheckoutFields
            customFields={catalog.widget.customFields}
            errors={customFieldErrors}
            fields={catalog.widget.fields}
            idPrefix={idPrefix}
            isPromoApplying={isRefreshingQuote}
            memberIdLabel={catalog.community?.memberIdLabel}
            onApplyPromo={controller.commands.applyPromo}
            onChange={handleFieldValuesChange}
            promoError={promoError}
            values={fieldValues}
          />
          <PaymentAction
            catalog={catalog}
            displayedTotalCents={displayedTotalCents}
            fieldValues={fieldValues}
            isBillingAddressReady={isBillingAddressReady}
            isPaymentReady={isPaymentReady}
            isRequiredShippingReady={isRequiredShippingReady}
            isSubmitting={isSubmitting}
            onFieldValuesChange={handleFieldValuesChange}
            quote={quote}
          />
        </fieldset>
      </div>
      {isSubmitting ? (
        <p
          className="lockerverse-payment__sr-only"
          ref={statusRef}
          role="status"
          tabIndex={-1}
        >
          {displayedTotalCents === 0
            ? "Completing order…"
            : "Processing payment…"}
        </p>
      ) : null}
      <PaymentErrorMessage message={paymentError} />
    </form>
  );
}

export function PaymentSkeleton() {
  return (
    <FormSkeleton
      fieldCount={2}
      label="Loading payment…"
      sectionCount={2}
      showCheckbox
    />
  );
}
